import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	map,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs';

// --- PrimeNG ---
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

// --- Services ---
import { CartService, type IGetCartParams } from '@services/cart.service';
import { LocalStorageService } from '@services/local-storage.service';

// --- Facades ---
import { AuthFacade } from '@facades/auth.facade';
import { ParametersFacade } from '@facades/parameters.facade';

// --- Stores ---
import { CartStore, type ICartLocalState } from '@stores/cart.store';

// --- Dialogs ---
import { OrdersErrorDialog } from '@pages/preferences/dialogs/orders-error-dialog/orders-error-dialog.component';

// --- Interfaces ---
import type { IParameters } from '@interfaces/parameters';
import type { IProduct } from '@interfaces/product';
import type { IPayment, IProductCart } from '@interfaces/productCart';

@Injectable({
	providedIn: 'root',
})
export class CartFacade {
	ref?: DynamicDialogRef;
	requesting: boolean = false;
	total: number = 0;
	totalItems!: number;
	updateCartReq = true;

	private readonly _refresh = new BehaviorSubject(undefined);

	private readonly _filter = new BehaviorSubject<IGetCartParams>({});
	readonly filter$ = this._filter.asObservable();

	private readonly handleRequest$ = combineLatest([
		this.filter$,
		this._refresh.asObservable(),
	]).pipe(switchMap(() => this.getCart(this._filter.value)));

	readonly cartState$ = combineLatest([
		this.cartStore.cartState$,
		this.handleRequest$,
	]).pipe(
		map(([state]) => state),
		shareReplay({ refCount: true })
	);

	readonly cartStateLocal$ = this.cartStore.cartStateLocal$.pipe(
		map(state => state),
		shareReplay({ refCount: true })
	);

	readonly authState$ = this.authFacade.authState$;

	constructor(
		private _messageService: MessageService,
		private authFacade: AuthFacade,
		private cartService: CartService,
		private cartStore: CartStore,
		private parametersFacade: ParametersFacade,
		private localStorageService: LocalStorageService,
		public _dialogService: DialogService
	) {
		this.getCartCache();
	}

	getPricePacking(
		defaultPriceListId: number,
		product: IProduct,
		packingId: number
	) {
		return product.packings
			.filter(packing => packing.id === packingId)[0]
			.prices.filter(price => price.priceListId === defaultPriceListId)[0]
			.price;
	}

	getCartCache() {
		const cart = this.localStorageService.get<ICartLocalState>('cart');
		if (!cart) {
			this.totalItems = 0;
			this.total = 0;

			return [];
		}

		this.totalItems = cart.totalItems;
		this.total = cart.total;

		return cart.products;
	}

	setTotalItems(products: IProductCart[]) {
		this.totalItems = 0;
		this.total = 0;

		setTimeout(() => {
			for (const product of products) {
				this.totalItems += product.quantity;
				this.total += product.quantity * product.unitPrice;
			}

			this.setCartSession(products);
		}, 1);
	}

	setItemsCart(
		products: IProductCart[],
		action?: 'add' | 'quantity' | 'remove'
	) {
		const messages = {
			add: 'Produto adicionado ao carrinho com sucesso.',
			quantity: 'Quantidade atualizada com sucesso.',
			remove: 'Item removido do carrinho com sucesso.',
		};

		this._messageService.clear();

		if (action === 'quantity') {
			this.setTotalItems(products);
			this.requesting = false;
			return;
		}

		this.saveCart(products).subscribe({
			next: items => {
				this.setTotalItems(items.result);
				this.requesting = false;

				if (action) {
					this._messageService.add({
						severity: 'success',
						summary: 'Carrinho Atualizado!',
						detail: messages[action || 'add'],
						icon: 'fa-solid fa-cart-shopping',
					});
				}
			},
			error: error => {
				this.requesting = false;

				if (action) {
					if (error.status === 400) {
						if (error?.error?.invalidItems?.length) {
							this.openErrorDialog(1, error.error.invalidItems);
						} else if (error?.error?.errors) {
							this._messageService.add({
								severity: 'error',
								summary: 'Houve um problema!',
								detail: error?.error?.errors?.length
									? error.error.errors[0]
									: 'Não foi possível atualizar o carrinho.',
							});
						} else if (error?.error?.messages?.length) {
							this.openErrorDialog(2, error.error.messages);
						}
					}
				}
			},
		});
	}

	openErrorDialog(type: number, error: any) {
		const dataObject = { action: 'saveCart' };
		this.ref = this._dialogService.open(OrdersErrorDialog, {
			header: 'Houve um problema.',
			modal: true,
			styleClass: 'border-round overflow-hidden',
			width: '600px',
			data:
				type === 1
					? { invalidItems: error, ...dataObject }
					: { messages: error, ...dataObject },
		});
	}

	updateCart() {
		this.cleanCartCache();

		setTimeout(() => {
			this.getCart()
				.pipe(shareReplay())
				.subscribe({
					next: res => {
						let currentCart = res.result;
						this.totalItems = 0;
						this.total = 0;

						currentCart.forEach((product: IProductCart) => {
							this.totalItems += product.quantity;
							this.total += product.quantity * product.unitPrice;
						});

						this.setCartSession(currentCart);
					},
					error: () => {},
				});
		}, 0);
	}

	setCartSession(products: IProductCart[]) {
		const cart: ICartLocalState = {
			products,
			totalItems: this.totalItems,
			total: this.total,
		};

		this.localStorageService.set('cart', cart);

		this.cartStore.updateCartLocal(cart);
	}

	addToCart(products: any[]) {
		if (this.isNotCustomer()) return;
		const parameters = this.localStorageService.get('parameters');

		this.requesting = true;
		const cartCache = this.getCartCache();
		const cart = this.getCart();
		let productsToAdd: IProductCart[] = [];

		for (const item of products) {
			const product: IProductCart = {
				quantity: item.quantity,
				productId: item.product.id,
				unitPrice: item.packingId
					? item.unitPrice
					: item.product.prices[0].price,
				product: item.product,
				packingId: item.packingId ? item.packingId : null,
			};

			if (this.parametersFacade.parameters?.stockDecimalPlaces === 0) {
				product.quantity =
					product.quantity < 1
						? Math.ceil(product.quantity)
						: Math.floor(product.quantity);
			}
			const bothTrue: any = [];

			const hasSome = cartCache.some(itemCartChart => {
				if (itemCartChart.productId === product.productId) {
					if (itemCartChart.packingId === product.packingId) {
						return true;
					} else {
						return false;
					}
				} else {
					return false;
				}
			});

			if (cartCache) {
				if (hasSome) {
					for (const productCache of cartCache)
						if (
							productCache.productId === product.productId &&
							productCache.packingId === product.packingId
						) {
							const stock = productCache?.product?.stocks[0]?.quantity;

							productCache.quantity += product.quantity;

							if (parameters) {
								if (!parameters.allowSellingWithoutStock) {
									productCache.quantity =
										productCache.quantity > stock
											? stock
											: productCache.quantity;
								}
							}
						}
				} else cartCache.push(product);

				productsToAdd = cartCache;

				continue;
			}

			cart.subscribe({
				next: items => {
					if (items.result.length) {
						if (hasSome) {
							for (const item of items.result)
								if (
									item.productId === product.productId &&
									item.packingId === product.packingId
								)
									item.quantity += product.quantity;
						} else items.result.push(product);
					} else productsToAdd = [product];
				},
				error: () => {
					this.requesting = false;
					this._messageService.add({
						severity: 'error',
						summary: 'Houve um problema!',
						detail: 'Não foi possível atualizar o carrinho.',
					});
				},
			});
		}

		this.setItemsCart(productsToAdd, 'add');
	}

	cleanCartCache() {
		this.localStorageService.remove('cart');
		this.totalItems = 0;
	}

	changeQtd(product: IProduct, quantity: number, packingId?: any) {
		this.requesting = true;
		let productObj: IProductCart = {
			quantity,
			productId: product.id,
			unitPrice: product.prices[0].price,
			product,
			packingId: packingId ? packingId : null,
		};
		delete productObj['id'];

		const parameters = this.localStorageService.get(
			'parameters'
		) as IParameters;

		const cart = this.getCartCache();

		if (cart && cart !== null) {
			for (const product of cart) {
				if (
					product.productId === productObj.productId &&
					product.packingId === productObj.packingId
				) {
					if (
						parameters?.cartQuantityLimitPerProduct &&
						parameters.cartQuantityLimitPerProduct > 0
					) {
						if (quantity > parameters.cartQuantityLimitPerProduct) {
							product.quantity = parameters.cartQuantityLimitPerProduct;

							this.openErrorDialog(2, [
								`A quantidade máxima permitida para este produto é ${parameters.cartQuantityLimitPerProduct}.`,
							]);
						} else {
							product.quantity = quantity;
						}
					} else {
						product.quantity = quantity;
					}
				}
			}

			this.setItemsCart(cart, 'quantity');
		}

		return cart;
	}

	removeProduct(productId: number, packingId?: any) {
		const cart = this.getCartCache();

		if (cart) {
			if (!packingId) {
				packingId = null;
			}

			const newItemsCart = cart.filter(p => {
				if (p.productId !== productId) return p;
				else if (p.packingId !== packingId) return p;

				return null;
			});

			this.setItemsCart(newItemsCart, 'remove');
			return newItemsCart;
		}

		return null;
	}

	getCart(queryParams?: IGetCartParams) {
		return this.cartService.getCart(queryParams).pipe(
			shareReplay(),
			tap(cart => this.cartStore.updateCart(cart))
		);
	}

	saveCart(cart: IProductCart[]) {
		return this.cartService.saveCart(cart).pipe(
			shareReplay(),
			tap(cart => this.cartStore.updateCart(cart))
		);
	}

	buyCart(
		cart: IProductCart[],
		obs: string,
		addressId: number,
		payment: IPayment,
		uuid: string
	) {
		return this.cartService.buyCart(cart, obs, addressId, payment, uuid);
	}

	isNotCustomer() {
		let isNotCustomer;
		this.authFacade.authState$.subscribe(user => {
			(user && user.user?.profileId === 3) || user.user?.profileId === 5
				? (isNotCustomer = true)
				: (isNotCustomer = false);
		});

		return isNotCustomer;
	}
}
